import {
  CreateGeneratedDocumentHiePdfCategory,
  GeneratedDocumentAdditionalClassification,
  GeneratedDocumentLabRecordType,
  GeneratedDocumentProcedureRecordType,
  GeneratedDocumentRecordSourceType,
  PreviousProviderDocumentKeyKind,
} from '@/feature/downloads/types';
import {
  labRecordType,
  ProcedureRecordType,
  SourceType,
  UnifiedAdditionalRecordClassificationType,
} from '@/feature/patients/types';
import {
  type DocumentKey,
  DocumentSource,
  DocumentType,
  MedicationDataType,
} from './types';

type CreateGeneratedDocumentInput =
  | {
      patientId: string;
      sourceRequestId?: string;
      kind: 'hie_pdf';
      hiePdf: { category: CreateGeneratedDocumentHiePdfCategory };
    }
  | {
      patientId: string;
      kind: 'previous_provider_document';
      previousProviderDocument: {
        documentKeyKind: PreviousProviderDocumentKeyKind;
        id: string;
        sourceType: GeneratedDocumentRecordSourceType;
        procedureRecordType?: GeneratedDocumentProcedureRecordType;
        labRecordType?: GeneratedDocumentLabRecordType;
        classificationType?: GeneratedDocumentAdditionalClassification;
      };
    }
  | {
      patientId: string;
      kind: 'medications_report';
      sourceRequestId?: string;
    };

type MapDocumentKeyToApiInputOptions = {
  entryKind?: 'pdf' | 'template';
  sourceRequestId?: string;
  medicationDataType?: MedicationDataType;
};

const sourceTypeMap: Partial<
  Record<SourceType, GeneratedDocumentRecordSourceType>
> = {
  [SourceType.PROVIDER_FACILITY_RETRIEVAL]:
    GeneratedDocumentRecordSourceType.ProviderFacilityRetrieval,
  [SourceType.MEDICATIONS_DIGITAL_DATA]:
    GeneratedDocumentRecordSourceType.MedicationsDigitalData,
  [SourceType.HIE_DATA]: GeneratedDocumentRecordSourceType.DigitalHealthDataHie,
};

const labRecordTypeMap: Partial<
  Record<labRecordType, GeneratedDocumentLabRecordType>
> = {
  [labRecordType.UNIFIED_LAB_RECORD_TYPE_LEGACY_LAB]:
    GeneratedDocumentLabRecordType.LegacyLab,
  [labRecordType.UNIFIED_LAB_RECORD_TYPE_PREVIOUS_PROVIDER]:
    GeneratedDocumentLabRecordType.PreviousProvider,
};

const procedureRecordTypeMap: Partial<
  Record<ProcedureRecordType, GeneratedDocumentProcedureRecordType>
> = {
  [ProcedureRecordType.PROCEDURE_NOTE]:
    GeneratedDocumentProcedureRecordType.ProcedureNote,
  [ProcedureRecordType.PROCEDURE]:
    GeneratedDocumentProcedureRecordType.Procedure,
};

const additionalClassificationMap: Partial<
  Record<
    UnifiedAdditionalRecordClassificationType,
    GeneratedDocumentAdditionalClassification
  >
> = {
  [UnifiedAdditionalRecordClassificationType.ALLERGIES]:
    GeneratedDocumentAdditionalClassification.Allergies,
  [UnifiedAdditionalRecordClassificationType.FAMILY_HISTORY]:
    GeneratedDocumentAdditionalClassification.FamilyHistory,
  [UnifiedAdditionalRecordClassificationType.SOCIAL_HISTORY]:
    GeneratedDocumentAdditionalClassification.SocialHistory,
  [UnifiedAdditionalRecordClassificationType.VACCINATIONS]:
    GeneratedDocumentAdditionalClassification.Vaccinations,
  [UnifiedAdditionalRecordClassificationType.ADDITIONAL_REFERENCE_DOCUMENTS]:
    GeneratedDocumentAdditionalClassification.AdditionalReferenceDocuments,
  [UnifiedAdditionalRecordClassificationType.PROVIDERS]:
    GeneratedDocumentAdditionalClassification.Providers,
};

const additionalClassificationToHiePdfCategoryMap: Partial<
  Record<
    UnifiedAdditionalRecordClassificationType,
    CreateGeneratedDocumentHiePdfCategory
  >
> = {
  [UnifiedAdditionalRecordClassificationType.ALLERGIES]:
    CreateGeneratedDocumentHiePdfCategory.Allergies,
  [UnifiedAdditionalRecordClassificationType.FAMILY_HISTORY]:
    CreateGeneratedDocumentHiePdfCategory.FamilyMemberHistories,
  [UnifiedAdditionalRecordClassificationType.SOCIAL_HISTORY]:
    CreateGeneratedDocumentHiePdfCategory.SocialHistories,
  [UnifiedAdditionalRecordClassificationType.VACCINATIONS]:
    CreateGeneratedDocumentHiePdfCategory.Immunizations,
  [UnifiedAdditionalRecordClassificationType.ADDITIONAL_REFERENCE_DOCUMENTS]:
    CreateGeneratedDocumentHiePdfCategory.DocumentReferences,
};

const hiePdfCategoryMap: Partial<
  Record<DocumentType, CreateGeneratedDocumentHiePdfCategory>
> = {
  [DocumentType.VITALS]: CreateGeneratedDocumentHiePdfCategory.Vitals,
  [DocumentType.LAB_RESULTS]: CreateGeneratedDocumentHiePdfCategory.Labs,
  [DocumentType.PROBLEMS]: CreateGeneratedDocumentHiePdfCategory.Problems,
  [DocumentType.MEDICATIONS]: CreateGeneratedDocumentHiePdfCategory.Medications,
  [DocumentType.PROCEDURES]: CreateGeneratedDocumentHiePdfCategory.Procedures,
  [DocumentType.ALLERGIES]: CreateGeneratedDocumentHiePdfCategory.Allergies,
  [DocumentType.VACCINATIONS]:
    CreateGeneratedDocumentHiePdfCategory.Immunizations,
  [DocumentType.ENCOUNTERS]: CreateGeneratedDocumentHiePdfCategory.Encounters,
};

const mapDocumentKeyToHiePdfInput = (
  key: DocumentKey,
  patientId: string,
  sourceRequestId?: string,
): CreateGeneratedDocumentInput | undefined => {
  const category = hiePdfCategoryMap[key.type];
  if (!category) return undefined;

  return {
    patientId,
    sourceRequestId,
    kind: 'hie_pdf',
    hiePdf: { category },
  };
};

export const mapDocumentKeyToApiInput = (
  key: DocumentKey,
  patientId: string,
  options: MapDocumentKeyToApiInputOptions = {},
): CreateGeneratedDocumentInput | undefined => {
  // entryKin is used for 'html' or 'pdf' render, for now, some 'curated' data still using 'pdf' render. Might be changed in the future.
  // TODO: Also think the documentKey is enough for backend to determine what type it's, so FE dont need to worry those branches.
  if (options.entryKind === 'template') {
    if (
      key.type === DocumentType.MEDICATIONS &&
      options.medicationDataType === MedicationDataType.MedicationsDigitalData
    ) {
      return {
        patientId,
        sourceRequestId: options.sourceRequestId,
        kind: 'medications_report',
      };
    }

    if (key.type === DocumentType.ADDITIONAL) {
      const category =
        additionalClassificationToHiePdfCategoryMap[key.classificationType];
      if (!category) return undefined;
      return {
        patientId,
        sourceRequestId: options.sourceRequestId,
        kind: 'hie_pdf',
        hiePdf: { category },
      };
    }
    return mapDocumentKeyToHiePdfInput(key, patientId, options.sourceRequestId);
  }

  // medical data is always HTML templates
  if (!options.entryKind && key.source === DocumentSource.MEDICAL_DATA) {
    return mapDocumentKeyToHiePdfInput(key, patientId);
  }

  if (key.source === DocumentSource.MEDICAL_RECORDS) {
    const mappedSourceType = sourceTypeMap[key.sourceType];
    if (!mappedSourceType) return undefined;

    const base = {
      patientId,
      kind: 'previous_provider_document' as const,
    };

    switch (key.type) {
      case DocumentType.VITALS:
        return {
          ...base,
          previousProviderDocument: {
            documentKeyKind: PreviousProviderDocumentKeyKind.Vital,
            id: key.vitalId,
            sourceType: mappedSourceType,
          },
        };

      case DocumentType.LAB_RESULTS:
        return {
          ...base,
          previousProviderDocument: {
            documentKeyKind: PreviousProviderDocumentKeyKind.Lab,
            id: key.labId,
            sourceType: mappedSourceType,
            labRecordType: key.recordType
              ? labRecordTypeMap[key.recordType]
              : undefined,
          },
        };

      case DocumentType.PROCEDURES:
        return {
          ...base,
          previousProviderDocument: {
            documentKeyKind: PreviousProviderDocumentKeyKind.Procedure,
            id: key.procedureId,
            sourceType: mappedSourceType,
            procedureRecordType: key.recordType
              ? procedureRecordTypeMap[key.recordType]
              : undefined,
          },
        };

      case DocumentType.IMAGING:
        return {
          ...base,
          previousProviderDocument: {
            documentKeyKind: PreviousProviderDocumentKeyKind.Imaging,
            id: key.imagingId,
            sourceType: mappedSourceType,
          },
        };

      case DocumentType.PATHOLOGY_RESULTS:
        return {
          ...base,
          previousProviderDocument: {
            documentKeyKind: PreviousProviderDocumentKeyKind.Pathology,
            id: key.pathologyId,
            sourceType: mappedSourceType,
          },
        };

      case DocumentType.MEDICATIONS:
        return {
          ...base,
          previousProviderDocument: {
            documentKeyKind: PreviousProviderDocumentKeyKind.Medication,
            id: key.medicationId,
            sourceType: mappedSourceType,
          },
        };

      case DocumentType.PROBLEMS:
        return {
          ...base,
          previousProviderDocument: {
            documentKeyKind: PreviousProviderDocumentKeyKind.Problem,
            id: key.problemId,
            sourceType: mappedSourceType,
          },
        };

      case DocumentType.PROVIDER_NOTES:
        return {
          ...base,
          previousProviderDocument: {
            documentKeyKind:
              PreviousProviderDocumentKeyKind.ProviderFacilityNote,
            id: key.providerNoteId,
            sourceType: mappedSourceType,
          },
        };

      case DocumentType.ADDITIONAL:
        return {
          ...base,
          previousProviderDocument: {
            documentKeyKind: PreviousProviderDocumentKeyKind.Additional,
            id: key.additionalRecordId,
            sourceType: mappedSourceType,
            classificationType:
              additionalClassificationMap[key.classificationType],
          },
        };

      case DocumentType.ENCOUNTERS:
        // TODO: PreviousProviderDocumentKeyKind is missing an Encounter value.
        // Add PreviousProviderDocumentKeyKind.Encounter to the enum and update
        // this case once the backend route supports it.
        return undefined;

      default:
        return undefined;
    }
  }

  return undefined;
};
